/**
 * The declarative system runtime — a `GameSystemRules` document, made playable.
 *
 * `MachinaModule` has accepted `type: 'game-system'` with a `rules` block since
 * the module format was written, and `@machina/sdk` validates it. Nothing has
 * ever interpreted it. A rules document today packs, publishes, installs, and
 * will not roll a die: every system that actually runs — 5e, Doomsworn — is a
 * hand-written TypeScript adapter in the monorepo, which means "add a system"
 * is still a pull request against Machina. This file is the piece that changes
 * that. Rules in, adapter out.
 *
 * THE TIER LINE, RESTATED HERE BECAUSE THIS IS WHERE IT IS ENFORCED: a Tier 2
 * system is data. It does arithmetic through {@link evaluateFormula} — a fixed
 * grammar with no property access and no host objects — and it reads dice
 * through an {@link OutcomeLadder}, an ordered list of comparisons. There is no
 * `eval`, no `new Function`, no dynamic import and no callback from the rules
 * document anywhere in this file, and that is the entire reason a stranger's
 * system can be installed without a sandbox. A system that needs to branch on
 * character state, spend a resource, or PAUSE for a decision (Doomsworn's Doom
 * Die) cannot be expressed this way and stays a code adapter.
 *
 * WHAT THIS REFUSES TO GUESS. Three rules, in descending order of how much
 * damage breaking them does:
 *
 *  1. Absent means absent. No `abilityModFormula` means no modifiers — 0, not
 *     `(score - 10) / 2`. No `combat` block means no tactical combat, so the
 *     adapter emits no `combat` surface and the UI grows none. No `defense`
 *     means the character has no defence number, not an armour class of 10.
 *     Every one of those defaults would make a system that declined to answer
 *     play as D&D, which is the exact failure EPIC-0045 exists to remove.
 *
 *  2. Fail at construction, not at the table. A resolution family with no
 *     ladder, a formula that will not parse, two abilities sharing an id — all
 *     of it throws from `adapterFromRules()`, naming the field. The table finds
 *     out when the system is installed, not halfway through a session.
 *
 *  3. Where the format has nothing to say, say nothing. `magic` and `balance`
 *     are deliberately not emitted: `MagicSystemSchema` names a casting style
 *     and no slot table, and `EncounterBalanceSchema` names a rating system and
 *     no maths. An adapter that returned empty slot lists and a fabricated
 *     difficulty would be worse than one that has no magic surface at all,
 *     because callers branch on the surface being missing.
 */
import { applyAffinity } from '../damage';
import { STANDARD_ARRAY_5E } from '../system-sheet';
import { rollDice } from '../utils/dice';
import { checkFormula, evaluateFormula, FormulaError } from './expression';
import { D20_ROLL_HIGH_LADDER, D20_ROLL_UNDER_LADDER, interpretAgainstLadder, PBTA_2D6_LADDER, } from './ladder';
import { overlayVariant, readingForRoll } from './resolution-variants';
// =============================================================================
// ERRORS
// =============================================================================
/**
 * A rules document cannot be turned into a working system.
 *
 * Always thrown from `adapterFromRules()` and never from a roll, and it names
 * the field at fault, because the person who can fix it is the author reading
 * the message — not the table that installed the module.
 */
export class DeclarativeSystemError extends Error {
    field;
    constructor(field, detail) {
        super(`Cannot build a game system from these rules — ${field}: ${detail}`);
        this.field = field;
        this.name = 'DeclarativeSystemError';
    }
}
// =============================================================================
// IDENTITY
// =============================================================================
/**
 * The registry id a rules document claims, from its `shortName`.
 *
 * Exported because a loader needs to know whether a system is already
 * registered before it pays to construct one.
 */
export function systemIdFromRules(rules) {
    const slug = rules.shortName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    if (!slug) {
        throw new DeclarativeSystemError('shortName', `"${rules.shortName}" contains no letters or digits, so it cannot become a system id`);
    }
    return slug;
}
function parseDiceSpec(expression) {
    const match = /^\s*(\d+)d(\d+|F)\s*$/i.exec(expression);
    if (!match)
        return null;
    const sides = match[2].toUpperCase() === 'F' ? 'F' : Number(match[2]);
    return { count: Number(match[1]), sides };
}
/**
 * Throw a Fate die pool.
 *
 * `rollDice` refuses a `dF`, and it is right to — there is no such die in its
 * table. Rather than reach for `Math.random` here (the one thing that would
 * make these rolls untestable and unseedable, unlike every other roll in the
 * engine), a d6 is folded into thirds: 1-2 → -1, 3-4 → 0, 5-6 → +1. That is the
 * Fate die's distribution exactly, and it keeps every roll in this file going
 * through the same helper.
 */
function rollFateDice(count) {
    const raw = rollDice(`${count}d6`).rolls.map((r) => r[0]);
    return raw.map((value) => (value <= 2 ? -1 : value <= 4 ? 0 : 1));
}
/** Every die a spec throws, as individual results. */
function throwDice(spec) {
    if (spec.sides === 'F')
        return rollFateDice(spec.count);
    return rollDice(`${spec.count}d${spec.sides}`).rolls.map((r) => r[0]);
}
function sum(values) {
    return values.reduce((a, b) => a + b, 0);
}
const CORE_FIELD = (field) => `coreResolution.${field}`;
/**
 * A pool reading that needs more information must have been given it.
 *
 * Checked here rather than at the point of use so the failure lands when the
 * module is installed. `count-successes` with no `successThreshold` would
 * otherwise count every die as a success and quietly make the system trivial,
 * and a `readKinds` naming a die the system never declared would read nothing
 * at all.
 */
function checkPoolReading(resolution, dice, at) {
    if (resolution.poolReading === 'count-successes' && resolution.successThreshold === undefined) {
        throw new DeclarativeSystemError(at('successThreshold'), 'a "count-successes" pool needs the value a die must reach to count as one');
    }
    // A ranked reading writes each die as digits after a decimal point, and a
    // Fate die of -1 has no digit. Refused rather than approximated, because a
    // wrong tie-break is invisible until two players compare pools.
    if (resolution.poolReading === 'ranked' && dice.sides === 'F') {
        throw new DeclarativeSystemError(at('poolReading'), 'a "ranked" reading needs dice numbered from 1 up, and Fate dice run -1 to +1');
    }
    if (resolution.readKinds) {
        const declared = resolution.poolKinds;
        if (!declared) {
            throw new DeclarativeSystemError(at('readKinds'), 'names kinds of die to read, but coreResolution.poolKinds declares none');
        }
        for (const kind of resolution.readKinds) {
            if (!declared.includes(kind)) {
                throw new DeclarativeSystemError(at('readKinds'), `"${kind}" is not a kind of die this system declares (poolKinds: ${declared.join(', ')})`);
            }
        }
    }
}
/**
 * The dice a system's checks are made with.
 *
 * `CoreResolution.type` names a family, not dice, so four of the families get
 * their obvious expression and the rest must say it outright. A `custom` system
 * that does not is refused here rather than at the table.
 */
function checkDiceFor(resolution, at) {
    if (resolution.diceExpression) {
        const spec = parseDiceSpec(resolution.diceExpression);
        if (!spec) {
            throw new DeclarativeSystemError(at('diceExpression'), `"${resolution.diceExpression}" is not a dice expression this runtime can throw (expected "NdM" or "NdF")`);
        }
        return spec;
    }
    switch (resolution.type) {
        case 'd20-roll-high':
        case 'd20-roll-under':
            return { count: 1, sides: 20 };
        case '2d6-pbta':
            return { count: 2, sides: 6 };
        case 'fate-dice':
            return { count: 4, sides: 'F' };
        case 'percentile':
        case 'd100':
            return { count: 1, sides: 100 };
        default:
            throw new DeclarativeSystemError(at('diceExpression'), `a "${resolution.type}" system has no dice this runtime can infer — set coreResolution.diceExpression, ` +
                'or ship a code adapter if the dice depend on the character (a pool, step dice)');
    }
}
function formatDice(spec) {
    return `${spec.count}d${spec.sides}`;
}
/**
 * Throw the dice for one roll.
 *
 * The pool's size comes from the situation, not the system: Trophy hands out
 * one light die per thing helping you, decided at the table. So a count on the
 * context wins over the expression's count, and a record on the context
 * (`{ light: 2, dark: 1 }`) throws each declared kind in the order the system
 * lists them. Every kind is the same die — the reading's `diceExpression` — and
 * differs only in how the fiction treats it.
 *
 * A record naming a kind the system does not declare throws. That is a caller
 * bug, and dropping the die would quietly change the result.
 */
function throwPool(spec, pool, poolKinds) {
    // Fate dice are a fixed four and have no pool to size.
    if (spec.sides === 'F') {
        return { dice: throwDice(spec), formula: formatDice(spec) };
    }
    if (typeof pool === 'object' && pool !== null) {
        if (!poolKinds) {
            throw new Error(`This system declares no kinds of die, so a pool of ${Object.keys(pool).join(', ')} dice ` +
                'cannot be thrown. Pass a count instead.');
        }
        for (const kind of Object.keys(pool)) {
            if (!poolKinds.includes(kind)) {
                throw new Error(`"${kind}" is not a kind of die this system declares (poolKinds: ${poolKinds.join(', ')})`);
            }
        }
        const dice = [];
        const kinds = [];
        const parts = [];
        for (const kind of poolKinds) {
            const count = Math.max(0, Math.floor(pool[kind] ?? 0));
            if (count === 0)
                continue;
            const thrown = throwDice({ count, sides: spec.sides });
            dice.push(...thrown);
            kinds.push(...thrown.map(() => kind));
            parts.push(`${count}d${spec.sides} ${kind}`);
        }
        return { dice, kinds, formula: parts.join(' + ') || `0d${spec.sides}` };
    }
    const count = typeof pool === 'number' && pool > 0 ? Math.floor(pool) : spec.count;
    const dice = throwDice({ count, sides: spec.sides });
    return {
        dice,
        // A bare count is dice of the first kind listed, so a system with kinds
        // can still be rolled by a caller that knows nothing about them.
        ...(poolKinds ? { kinds: dice.map(() => poolKinds[0]) } : {}),
        formula: formatDice({ count, sides: spec.sides }),
    };
}
/**
 * A pool as one number that orders the way the pool does.
 *
 * The highest die goes before the decimal point and the rest follow it,
 * highest first, each padded to the width of the die's largest face: [6, 5, 5]
 * is 6.55 and [6, 6] is 6.6, so the pair of sixes wins, which is Trophy's
 * "count the sixes, then the fives" rule. The whole number is still the highest
 * die, so a rung reading `natural >= 6` means what it meant before.
 *
 * A double holds about fifteen significant digits. Dice past that do not break
 * ties, which for a d6 pool is anything after the fourteenth die.
 */
function rankPool(dice, sides) {
    const [first, ...rest] = [...dice].sort((a, b) => b - a);
    if (first === undefined)
        return 0;
    const width = String(sides).length;
    const room = Math.max(0, Math.floor((15 - String(first).length) / width));
    const tail = rest
        .slice(0, room)
        .map((die) => String(die).padStart(width, '0'))
        .join('');
    return tail ? Number(`${first}.${tail}`) : first;
}
/**
 * Reduce a throw to the single number the outcome ladder reads.
 *
 * Summing was the unstated default until a system arrived that does not sum.
 * Everything the runtime had seen — 2d6, 4dF, a lone d20 — adds its dice, so
 * nothing revealed that adding was a choice rather than the only option.
 * Trophy Dark reads the highest die of its pool, and summing three 2s would
 * have produced a 6: the system's best result, from its worst throw.
 *
 * The pool is narrowed first — to the kinds the reading names, then to the
 * highest few — and only what is left is read. `read` is returned so the table
 * can be shown which dice counted.
 */
function readPool(dice, kinds, res, sides) {
    let read = res.readKinds && kinds ? dice.filter((_, i) => res.readKinds.includes(kinds[i])) : [...dice];
    if (res.keepHighest !== undefined) {
        read = [...read].sort((a, b) => b - a).slice(0, res.keepHighest);
    }
    if (read.length === 0)
        return { value: 0, read };
    switch (res.poolReading) {
        case 'highest':
            return { value: Math.max(...read), read };
        case 'lowest':
            return { value: Math.min(...read), read };
        case 'count-successes': {
            // Threshold checked at construction, so this cannot be undefined here.
            const threshold = res.successThreshold;
            return { value: read.filter((die) => die >= threshold).length, read };
        }
        case 'ranked':
            // Fate dice are refused for this reading at construction.
            return { value: rankPool(read, sides), read };
        case 'sum':
        default:
            return { value: sum(read), read };
    }
}
/**
 * Roll a formula that mixes dice with arithmetic — `"1d20 + dex_mod"`.
 *
 * The dice are thrown first and substituted as numbers, then the remainder goes
 * through `evaluateFormula` unchanged. Splitting it this way is deliberate:
 * the expression grammar stays arithmetic-only and gains no dice capability, so
 * the reasons it is safe to run a stranger's formula do not change.
 */
function rollFormula(formula, scope) {
    const dice = [];
    const substituted = formula.replace(/(?<![A-Za-z0-9_])(\d*)d(\d+|F)(?![A-Za-z0-9_])/gi, (_whole, count, sides) => {
        const spec = parseDiceSpec(`${count || '1'}d${sides}`);
        const rolled = throwDice(spec);
        dice.push(...rolled);
        return `(${sum(rolled)})`;
    });
    const total = evaluateFormula(substituted, scope);
    return { total, dice, natural: sum(dice) };
}
/** The identifiers a formula rolled against a character may use. */
function formulaScopeKeys(abilities) {
    return ['level', ...abilities.flatMap((a) => [a.id, `${a.id}_mod`])];
}
// =============================================================================
// LADDERS
// =============================================================================
/**
 * A d20 ladder honouring a declared crit and fumble range.
 *
 * Built only when the system actually declares one. A system that says nothing
 * gets the shipped constant verbatim, so the differential test against the
 * hand-written 5e adapter (ISS-0149) keeps comparing like with like — and a
 * system that says `critRange: [19, 20]` gets a ladder that crits on 19, rather
 * than having the field quietly ignored, which is the same class of bug as
 * substituting a default.
 */
function d20LadderFor(resolution, direction) {
    const ready = direction === 'high' ? D20_ROLL_HIGH_LADDER : D20_ROLL_UNDER_LADDER;
    if (!resolution.critRange && !resolution.fumbleRange)
        return ready;
    const crits = resolution.critRange ?? (direction === 'high' ? [20] : [1]);
    const fumbles = resolution.fumbleRange ?? (direction === 'high' ? [1] : [20]);
    const beat = direction === 'high' ? '>=' : '<=';
    const against = direction === 'high' ? 'DC {target}' : '{target}';
    return {
        rungs: [
            ...crits.map((value) => ({
                measure: 'natural',
                op: '==',
                value,
                outcome: 'critical-success',
                description: `Natural ${value}! Critical success (total {total} vs ${against})`,
            })),
            ...fumbles.map((value) => ({
                measure: 'natural',
                op: '==',
                value,
                outcome: 'critical-failure',
                description: `Natural ${value}! Critical failure (total {total} vs ${against})`,
            })),
            {
                measure: 'total',
                op: beat,
                value: 'target',
                outcome: 'success',
                description: `Success ({total} vs ${against})`,
            },
        ],
        fallback: { outcome: 'failure', description: `Failure ({total} vs ${against})` },
        ...(ready.defaultTarget === undefined ? {} : { defaultTarget: ready.defaultTarget }),
    };
}
/**
 * How this system reads its dice.
 *
 * An author-supplied ladder always wins — it is how a d20 system states PF2e's
 * four degrees, and the only way a percentile or Fate system says anything at
 * all. Where none is supplied and the runtime has no ready-made ladder for the
 * family, this throws: the alternative is deciding on the author's behalf what
 * a 78 on percentile dice means, and the engine's only available default is
 * d20's.
 */
function ladderFor(resolution, at) {
    if (resolution.outcomeLadder)
        return resolution.outcomeLadder;
    switch (resolution.type) {
        case 'd20-roll-high':
            return d20LadderFor(resolution, 'high');
        case 'd20-roll-under':
            return d20LadderFor(resolution, 'under');
        case '2d6-pbta':
            // PbtA has no natural-die rule to honour, so a declared crit range here
            // is a statement the ready-made ladder cannot carry. Refusing beats
            // dropping it, since the author would never see it go missing.
            if (resolution.critRange || resolution.fumbleRange) {
                throw new DeclarativeSystemError(at('critRange'), 'a 2d6 PbtA ladder reads totals, not natural dice, so a crit or fumble range cannot be ' +
                    'applied — state the whole thing in coreResolution.outcomeLadder instead');
            }
            return PBTA_2D6_LADDER;
        default:
            throw new DeclarativeSystemError(at('outcomeLadder'), `this runtime ships no outcome ladder for "${resolution.type}", so the rules must supply one — ` +
                'an ordered list of comparisons saying what each result means');
    }
}
/**
 * The interpretation returned when the ladder declines.
 *
 * `interpretAgainstLadder` returns null when every rung needs a target the roll
 * does not carry — a check with no DC set is a number waiting for a GM, and the
 * ladder refuses to invent one. `GameSystemAdapter.interpretResult` has no null
 * to return, and the two obvious ways out are both worse than this one:
 * throwing breaks `resolveRoll`, which asks every roll about criticality even
 * when no DC was given, and returning `failure` states the exact lie the ladder
 * declined to state.
 *
 * So: `undecided`, an outcome of its own. It was tempting to reuse `partial`,
 * but that is a real result in a PbtA game — "yes, but", which a sheet may draw
 * in amber — and borrowing it here would show a player a cost they never paid.
 * `succeeded()` reads `undecided` as false and neither criticality helper
 * fires, so a caller that ignores it behaves correctly.
 */
function undecided(roll) {
    return {
        outcome: 'undecided',
        description: `Rolled ${roll.total} — no target number was set, so this roll has not been judged.`,
        meta: { undecided: true, reason: 'no-target' },
    };
}
function compileReading(resolution, at) {
    // Ladder first: a family with no ladder is the most common authoring gap,
    // and the one the error message most needs to name.
    const ladder = ladderFor(resolution, at);
    const dice = checkDiceFor(resolution, at);
    checkPoolReading(resolution, dice, at);
    return { resolution, dice, ladder };
}
/**
 * A rung no roll can reach is a rule the author believes is in force and is
 * not, so it is refused.
 *
 * This is where `appliesTo` on a rung and a variant meet. A rung in the core
 * ladder limited to `hunt` is fine until a variant claims `hunt` and brings its
 * own ladder, at which point the rung is dead. A rung inside a variant's ladder
 * limited to a roll type that variant never reads is dead from the start.
 */
function checkRungsReachable(ladder, reachable, path, why) {
    ladder.rungs.forEach((rung, index) => {
        if (rung.appliesTo && !rung.appliesTo.some(reachable)) {
            throw new DeclarativeSystemError(`${path}.rungs[${index}].appliesTo`, why);
        }
    });
}
/**
 * Every reading a rules document declares, checked once and indexed by roll
 * type, so a roll finds its reading with one lookup and nothing about a
 * variant can fail mid-session.
 */
function compileReadings(core) {
    const variants = core.variants ?? [];
    if (core.poolKinds) {
        assertUniqueIds(core.poolKinds.map((id) => ({ id })), 'coreResolution.poolKinds');
    }
    assertUniqueIds(variants, 'coreResolution.variants');
    const base = compileReading(readingForRoll(core, undefined).resolution, CORE_FIELD);
    const byType = new Map();
    const claimedBy = new Map();
    variants.forEach((variant, index) => {
        const path = `coreResolution.variants[${index}]`;
        // A value the core block supplied and the variant inherited is the core
        // block's fault. Anything else, including a field nobody set that this
        // reading needs, is the variant's to fix.
        const at = (field) => variant[field] === undefined &&
            core[field] !== undefined
            ? `coreResolution.${field}`
            : `${path}.${field}`;
        const reading = { ...compileReading(overlayVariant(core, variant), at), variantId: variant.id };
        for (const rollType of variant.appliesTo) {
            const earlier = claimedBy.get(rollType);
            if (earlier) {
                throw new DeclarativeSystemError(`${path}.appliesTo`, `"${rollType}" is already read by variant "${earlier.id}", and a roll type can have only one reading`);
            }
            claimedBy.set(rollType, variant);
            byType.set(rollType, reading);
        }
        if (variant.outcomeLadder) {
            checkRungsReachable(variant.outcomeLadder, (rollType) => variant.appliesTo.includes(rollType), `${path}.outcomeLadder`, `this rung is limited to roll types variant "${variant.id}" does not read ` +
                `(it reads ${variant.appliesTo.join(', ')}), so it can never fire`);
        }
    });
    if (core.outcomeLadder) {
        checkRungsReachable(core.outcomeLadder, (rollType) => !claimedBy.get(rollType)?.outcomeLadder, 'coreResolution.outcomeLadder', 'every roll type this rung is limited to is read by a variant with its own ladder, so it ' +
            'can never fire. Move it into that variant\'s ladder.');
    }
    return { base, byType };
}
// =============================================================================
// CHARACTER SCOPE
// =============================================================================
/**
 * The numbers a formula may see for a given character.
 *
 * A score the character does not have stays out of scope, so a formula naming
 * it fails loudly; its modifier is 0, because a missing ability is a creature
 * with no bonus rather than a creature with a score of 0 (which in a d20 system
 * is -5 applied to everything). `toSystemAbilities` draws the same line for the
 * same reason.
 */
function characterScope(abilities, scores, modifierOf, level) {
    const scope = { level: level ?? 1 };
    for (const ability of abilities) {
        const score = scores?.[ability.id];
        if (typeof score === 'number' && Number.isFinite(score)) {
            scope[ability.id] = score;
            scope[`${ability.id}_mod`] = modifierOf(score);
        }
        else {
            scope[`${ability.id}_mod`] = 0;
        }
    }
    return scope;
}
/** Rough health banding, sharing the vocabulary the 5e adapter already emits. */
function healthStatus(current, max) {
    if (current <= 0)
        return 'unconscious';
    if (max <= 0)
        return 'healthy';
    const ratio = current / max;
    if (ratio > 0.75)
        return 'healthy';
    if (ratio > 0.5)
        return 'wounded';
    if (ratio > 0.25)
        return 'bloodied';
    return 'critical';
}
// =============================================================================
// DM GUIDANCE
// =============================================================================
/**
 * What to tell the GM about magic, which depends on a distinction the runtime
 * cannot paper over: declaring magic and specifying it are different things.
 *
 * A rules document that declares `spell-slots` and three casting abilities
 * produces an adapter with NO magic surface (see the block at the end of
 * `adapterFromRules`), because a style name is not a slot table. Saying nothing
 * in that case was the dangerous outcome — the model reads "spell slots",
 * finds no rules, and supplies 5e's. So the guidance states the shortfall
 * rather than leaving the silence to be filled.
 */
function magicMistakes(rules) {
    // A declared `type: 'none'` is a system saying it has no magic, and reads
    // exactly like no magic block at all.
    if (!rules.magic || rules.magic.type === 'none') {
        return ['This system declares no magic. Do not offer spells or spell slots.'];
    }
    const abilities = rules.magic.castingAbilities?.length
        ? ` cast off ${rules.magic.castingAbilities.join(', ')}`
        : '';
    return [
        `This system declares ${rules.magic.type} magic${abilities}, but no maths for it: no slot ` +
            'table, no cost, and no spell save DC. The engine computes none of those and will not ' +
            'invent them. Rule spending and resistance at the table, and do not quote a DC or a slot ' +
            'count this system has not given you.',
        ...(rules.magic.concentrationRule
            ? []
            : ['This system has no concentration rule. A caster may hold as many effects as they can cast.']),
    ];
}
/**
 * What the AI GM is told about a system whose author wrote no guidance.
 *
 * Assembled from what the document does declare and explicit about what it does
 * not, rather than left as empty strings. A blank `systemBrief` reaching the
 * prompt is how a GM ends up narrating 5e at a table playing something else —
 * the model fills the silence with the system it knows best.
 */
function guidanceFrom(rules, resolution) {
    const runner = rules.gamemasterTitle ?? 'Game Master';
    const abilities = rules.abilities.map((a) => a.name).join(', ') || 'no ability scores';
    // Named, because a model told only the default reading will apply it to a
    // Contest or a Combat Roll too, and the engine will then disagree with it.
    const variants = resolution.variants?.length
        ? ` Some rolls read the dice their own way: ${resolution.variants
            .map((v) => `${v.name ?? v.id} (${v.appliesTo.join(', ')})`)
            .join('; ')}.`
        : '';
    return {
        systemBrief: `${rules.name}${rules.publisher ? ` (${rules.publisher})` : ''} resolves actions with ` +
            `${resolution.type}.${variants} Characters are described by: ${abilities}. The person running the ` +
            `game is called the ${runner}. This system shipped no written guidance, so describe only ` +
            'what these rules state and ask the table when something is not covered.',
        combatNorms: rules.combat
            ? `Combat is turn-based${rules.combat.roundDuration ? `, a round being ${rules.combat.roundDuration}` : ''}` +
                `${rules.combat.gridBased ? `, measured on a grid in ${rules.combat.gridUnit}` : ', played without a grid'}.` +
                `${rules.combat.deathMechanic ? ` Dropping to zero is handled by: ${rules.combat.deathMechanic}.` : ''}`
            : 'This system declares no combat framework. Do not roll initiative or track hit points; ' +
                'resolve violence the way the fiction and the core resolution allow.',
        socialNorms: 'This system states no separate social rules. Use the core resolution for social conflict ' +
            'and do not import another system\'s skill list.',
        toneGuidance: 'No tone was declared. Take the tone from the table and the module content rather than ' +
            'assuming heroic fantasy.',
        commonMistakes: [
            `Do not apply D&D rules this system has not declared — its resolution is ${resolution.type}.`,
            ...(rules.abilityModFormula
                ? []
                : ['This system declares no ability modifier formula, so ability scores grant no bonus.']),
            ...magicMistakes(rules),
        ],
    };
}
// =============================================================================
// CONSTRUCTION-TIME CHECKS
// =============================================================================
function assertUniqueIds(items, field) {
    const seen = new Set();
    for (const item of items) {
        if (!item.id)
            throw new DeclarativeSystemError(field, 'an entry has an empty id');
        if (seen.has(item.id)) {
            throw new DeclarativeSystemError(field, `duplicate id "${item.id}"`);
        }
        seen.add(item.id);
    }
}
/**
 * Check a formula the way it will be rolled, dice and all.
 *
 * `checkFormula` alone would reject `"1d20 + dex_mod"` as an unknown identifier,
 * because the dice have not been substituted yet. Stripping them to a constant
 * first checks exactly the arithmetic that will run.
 */
function checkRollableFormula(formula, scopeKeys, field) {
    const stripped = formula.replace(/(?<![A-Za-z0-9_])(\d*)d(\d+|F)(?![A-Za-z0-9_])/gi, '(1)');
    const result = checkFormula(stripped, scopeKeys);
    if (!result.valid) {
        throw new DeclarativeSystemError(field, result.error);
    }
}
/**
 * Build a working `GameSystemAdapter` from a rules document.
 *
 * Throws {@link DeclarativeSystemError} for anything that would otherwise fail
 * mid-session: a resolution family with no ladder, a formula that will not
 * parse, duplicate ability ids.
 */
export function adapterFromRules(rules, options = {}) {
    // --- Construction-time validation. Everything that can be wrong, found here.
    const id = options.id ?? systemIdFromRules(rules);
    const resolution = rules.coreResolution;
    assertUniqueIds(rules.abilities, 'abilities');
    if (rules.classes)
        assertUniqueIds(rules.classes, 'classes');
    if (rules.conditions)
        assertUniqueIds(rules.conditions, 'conditions');
    const abilities = rules.abilities;
    const classes = rules.classes ?? [];
    const conditions = rules.conditions ?? [];
    if (rules.abilityModFormula) {
        const check = checkFormula(rules.abilityModFormula, ['score']);
        if (!check.valid)
            throw new DeclarativeSystemError('abilityModFormula', check.error);
    }
    // Throws when the document cannot answer, which is the whole point of doing
    // it here rather than on the first roll of the session. Every variant is
    // checked too, including the ones a table may never roll.
    const readings = compileReadings(resolution);
    const readingFor = (rollType) => readings.byType.get(rollType) ?? readings.base;
    const scopeKeys = formulaScopeKeys(abilities);
    if (rules.combat?.initiativeFormula) {
        checkRollableFormula(rules.combat.initiativeFormula, scopeKeys, 'combat.initiativeFormula');
    }
    // --- Behaviour.
    /**
     * Absent formula means absent modifier. Flooring at the point of use rather
     * than in the grammar, per the note in `expression.ts`: `(score - 10) / 2` is
     * integer division in every system that writes it, and a modifier of 2.5 is
     * nobody's intent. A formula that wants other rounding says `ceil(...)` or
     * `round(...)` itself, and flooring an integer changes nothing.
     */
    const modifierOf = (score) => {
        if (!rules.abilityModFormula)
            return 0;
        return Math.floor(evaluateFormula(rules.abilityModFormula, { score }));
    };
    const rollCheck = (context) => {
        const reading = readingFor(context.type);
        const spec = reading.dice;
        const modifier = context.modifier ?? 0;
        // Advantage is a single-die idea: keeping the higher of two is meaningless
        // for 2d6 and 4dF, where the swing would silently change the curve. Same
        // rule the animated path applies in `apps/web/src/lib/system-roll.ts`, and
        // holding both cancels rather than stacking. A pool split into kinds is
        // never one die, whatever the expression says.
        const wantAdvantage = !!context.advantage && !context.disadvantage;
        const wantDisadvantage = !!context.disadvantage && !context.advantage;
        const kindedPool = typeof context.pool === 'object' && context.pool !== null;
        const canSwing = (wantAdvantage || wantDisadvantage) && spec.count === 1 && spec.sides !== 'F' && !kindedPool;
        let dice;
        let natural;
        let kinds;
        let read;
        let base;
        if (canSwing) {
            dice = throwDice({ count: 2, sides: spec.sides });
            natural = wantAdvantage ? Math.max(...dice) : Math.min(...dice);
            base = `2d${spec.sides}${wantAdvantage ? 'kh1' : 'kl1'}`;
        }
        else {
            const thrown = throwPool(spec, context.pool, resolution.poolKinds);
            dice = thrown.dice;
            kinds = thrown.kinds;
            base = thrown.formula;
            const result = readPool(dice, kinds, reading.resolution, spec.sides);
            natural = result.value;
            // Shown only when the reading set dice aside, so an ordinary roll's meta
            // does not repeat its own dice back.
            if (result.read.length !== dice.length)
                read = result.read;
        }
        const formula = modifier === 0
            ? base
            : `${base} ${modifier > 0 ? '+' : '-'} ${Math.abs(modifier)}`;
        return {
            total: natural + modifier,
            dice,
            modifier,
            formula,
            natural,
            meta: {
                type: context.type,
                ability: context.ability,
                skill: context.skill,
                dc: context.dc,
                // Normalised rather than echoed: asking for both cancels to a straight
                // roll, and a system whose dice cannot swing never got one.
                advantage: canSwing && wantAdvantage,
                disadvantage: canSwing && wantDisadvantage,
                systemId: id,
                ...(reading.variantId === undefined ? {} : { variant: reading.variantId }),
                ...(kinds === undefined ? {} : { kinds }),
                ...(read === undefined ? {} : { read }),
            },
        };
    };
    // Chosen by the CONTEXT's type rather than the roll's meta, so a roll made
    // somewhere else — the animated dice tray — is read the same way.
    const interpretResult = (roll, context) => interpretAgainstLadder(readingFor(context.type).ladder, roll, context) ?? undecided(roll);
    const template = rules.characterTemplate;
    const adapter = {
        id,
        name: rules.name,
        version: options.version ?? '0.0.0',
        rules,
        // --- Character lifecycle ---
        createCharacter(request) {
            // Only scores the caller actually supplied. An ability left out stays out
            // rather than arriving as a 10 — the format states no starting score, and
            // a silent 10 is a d20 assumption wearing a neutral face.
            const scores = {};
            for (const ability of abilities) {
                const score = request.abilities?.[ability.id];
                if (typeof score === 'number' && Number.isFinite(score))
                    scores[ability.id] = score;
            }
            const level = template.progressionType === 'freeform' ? undefined : (request.level ?? 1);
            // A classless system produces a character with no classes at all, and a
            // class-based one only names a class the caller chose. Defaulting to a
            // fighter would be inventing a character sheet.
            const classId = typeof request.classId === 'string' ? request.classId : undefined;
            const characterClasses = template.usesClasses && classId
                ? [{ classId, level: level ?? 1 }]
                : undefined;
            // Starting health, in order of how much the source knows: what the caller
            // passed, then the chosen class's hit die, then nothing. Nothing is a
            // real answer for a wound-levels or stress-track system, and
            // `validateCharacter` says so for one that claims hit points.
            const chosenClass = classId ? classes.find((c) => c.id === classId) : undefined;
            const maxHp = typeof request.maxHp === 'number'
                ? request.maxHp
                : (chosenClass?.hitDie ?? 0);
            const character = {
                id: crypto.randomUUID(),
                name: request.name,
                systemId: id,
                abilities: scores,
                health: { current: maxHp, max: maxHp },
                conditions: [],
            };
            if (level !== undefined)
                character.level = level;
            if (characterClasses)
                character.classes = characterClasses;
            // No `defense` and no `speed` unless the caller gave one. `defenseModel:
            // 'armor-class'` names a style, not a formula, so there is no number to
            // compute and a 10 here would read as a real armour class.
            if (typeof request.defense === 'number')
                character.defense = request.defense;
            if (typeof request.speed === 'number')
                character.speed = request.speed;
            return character;
        },
        validateCharacter(character) {
            const errors = [];
            const warnings = [];
            const declared = new Set(abilities.map((a) => a.id));
            for (const ability of abilities) {
                if (typeof character.abilities?.[ability.id] !== 'number') {
                    warnings.push(`No score for ${ability.name} (${ability.id})`);
                }
            }
            for (const key of Object.keys(character.abilities ?? {})) {
                if (!declared.has(key)) {
                    warnings.push(`"${key}" is not an ability this system declares`);
                }
            }
            if (character.level !== undefined) {
                if (character.level < 1)
                    errors.push(`Level ${character.level} is below 1`);
                if (template.levelCap !== undefined && character.level > template.levelCap) {
                    errors.push(`Level ${character.level} exceeds this system's cap of ${template.levelCap}`);
                }
            }
            if (character.classes?.length) {
                if (!template.usesClasses) {
                    errors.push('This system is classless, but the character carries classes');
                }
                for (const entry of character.classes) {
                    if (classes.length && !classes.some((c) => c.id === entry.classId)) {
                        errors.push(`Unknown class "${entry.classId}"`);
                    }
                }
            }
            for (const condition of character.conditions ?? []) {
                if (conditions.length && !conditions.some((c) => c.id === condition.conditionId)) {
                    warnings.push(`"${condition.conditionId}" is not a condition this system declares`);
                }
            }
            // A hit-points system with no hit points is broken; a wound-levels or
            // stress-track system legitimately has no such number, and flagging that
            // as an error would make every character in it invalid.
            if (character.health.max <= 0) {
                const message = `Max health is ${character.health.max}`;
                if (template.healthModel === 'hit-points') {
                    errors.push(`${message}, but this system uses hit points`);
                }
                else {
                    warnings.push(`${message} (health model: ${template.healthModel})`);
                }
            }
            return { valid: errors.length === 0, errors, warnings };
        },
        getAbilityModifier: modifierOf,
        getAbilities: () => abilities,
        getClasses: () => classes,
        getConditions: () => conditions,
        // --- Dice & resolution ---
        rollCheck,
        interpretResult,
        // --- Character surface ---
        characterSurface: {
            getCreationSpec() {
                // The system's own abilities, in its own order, with one value each.
                //
                // THE 5e LEAK, STATED OUT LOUD: the array itself is 15/14/13/12/10/8,
                // because `GameSystemRules` has nowhere to declare a starting-score
                // scale. That is the same fallback `creationSpecFor` already applies to
                // any adapter, so declaring it here changes nothing except getting the
                // ability list and its length right — but a PbtA system whose stats run
                // -1 to +3 will still be handed d20 numbers until the format can say
                // otherwise. See the Phase 2 report.
                const standardArray = [...STANDARD_ARRAY_5E];
                while (standardArray.length < abilities.length)
                    standardArray.push(10);
                return {
                    abilities,
                    standardArray: standardArray.slice(0, abilities.length),
                    defaultScore: 10,
                    rolledPoolSize: Math.max(abilities.length, 1),
                    ...(rules.abilityModFormula
                        ? {}
                        : {
                            note: `${rules.name} derives no modifier from an ability score — these numbers are read directly.`,
                        }),
                };
            },
            // Nothing. A declarative system has no way to describe a bespoke tracker:
            // Doomsworn's Debt, Liens and Doom Clock all exist because a code adapter
            // could build `SheetField`s for them, and `GameSystemRules` has no
            // equivalent. Emitting the abilities again as a "section" would only
            // duplicate what the sheet already draws.
            getSheetSections: () => [],
            // Always undefined. `GameSystemRules` has a class hit die but no rule for
            // turning it into starting health: "max die plus CON at level 1" is 5e's
            // rule, and Trophy has no hit points at all. The wizard asks the player
            // for the number instead.
            getStartingHealth: () => undefined,
        },
        getDmGuidance: () => rules.dmGuidance ?? guidanceFrom(rules, resolution),
    };
    // --- Combat, only when the system says it has any -------------------------
    //
    // `adapter.combat` being undefined is how the UI knows not to grow an
    // initiative tracker and a monster panel (`systemHasCombat()`), so a rules
    // document with no `combat` block must produce an adapter with no combat
    // surface. This is the one place that decision is made.
    if (rules.combat) {
        const combat = rules.combat;
        adapter.combat = {
            rollInitiative(combatant) {
                if (!combat.initiativeFormula) {
                    // Declared combat, undeclared initiative: everyone acts on the same
                    // number and the order is whatever the GM chooses, which is honest
                    // for a system that never said how to sort a round.
                    return 0;
                }
                const scope = characterScope(abilities, combatant.character?.abilities, modifierOf, combatant.character?.level);
                return rollFormula(combat.initiativeFormula, scope).total;
            },
            getActionEconomy(_combatant) {
                return {
                    actions: (combat.actionEconomy ?? []).map((action) => ({
                        typeId: action.id,
                        remaining: action.countPerRound,
                        // The format lists what kinds of action exist and never what a
                        // creature may do with one, so there is nothing to offer here.
                        options: [],
                    })),
                };
            },
            /**
             * An attack, as far as a rules document can describe one.
             *
             * `GameSystemRules` states no attack bonus and no damage: a declarative
             * system can say "roll the core dice against the target's defence" and
             * nothing more. So that is exactly what this does, and it returns no
             * damage rather than inventing a 1d8 — the GM applies the damage the
             * fiction calls for, and `meta.declarative` says why the field is empty.
             * Giving the format a damage model is ISS-0154's job and is deliberately
             * not attempted here.
             *
             * THE ONE EXCEPTION, and why it is not a damage model. A
             * {@link CombatAction} carrying `damageDice` is the CALLER's number, not
             * the system's: a monster's stat-block action, a weapon the GM typed in.
             * Refusing to roll dice the caller already has in its hand would not be
             * principled, it would just push `2d6` into the UI to be rolled with a
             * different RNG. So it is rolled — and `meta.damageModel` says where it
             * came from, so nobody reads the number as something the system declared.
             */
            resolveAttack(attacker, target, action) {
                const spec = typeof action === 'string' ? undefined : action;
                const actionName = typeof action === 'string' ? action : action.name;
                const context = {
                    type: 'attack',
                    systemId: id,
                    // An action that states its own total bonus outranks the combatant's
                    // standing modifier, for the same reason as the damage below: the
                    // caller knows what it is swinging and this system does not.
                    modifier: spec?.attackBonus ?? (typeof attacker.meta?.attackModifier === 'number'
                        ? attacker.meta.attackModifier
                        : 0),
                    advantage: attacker.meta?.advantage === true,
                    disadvantage: attacker.meta?.disadvantage === true,
                    ...(target.character?.defense === undefined ? {} : { dc: target.character.defense }),
                };
                const roll = rollCheck(context);
                const interpretation = interpretResult(roll, context);
                const hit = interpretation.outcome === 'success' || interpretation.outcome === 'critical-success';
                // Said plainly, because "no damage" from an attack looks like a bug
                // until you know the format cannot express one.
                let damageModel = 'none — this system declares no damage rules';
                let damage;
                if (hit && spec?.damageDice) {
                    const scope = characterScope(abilities, attacker.character?.abilities, modifierOf, attacker.character?.level);
                    try {
                        const rolled = rollFormula(spec.damageDice, scope);
                        damage = {
                            total: Math.max(0, rolled.total),
                            // The rules document names no damage types, so an action that
                            // does not name one leaves the hit untyped. `untyped` matches no
                            // affinity entry, which is the honest outcome.
                            type: spec.damageType ?? 'untyped',
                            dice: spec.damageDice,
                            ...(spec.magical === undefined ? {} : { magical: spec.magical }),
                        };
                        damageModel = `caller-supplied — ${rules.name} declares no damage rules; "${spec.damageDice}" came from the action, not from the system`;
                    }
                    catch (error) {
                        // A damage expression the caller wrote and this runtime cannot
                        // parse is a caller bug, and it must not take the attack roll down
                        // with it: the hit still happened.
                        damageModel = `none — the action's damage expression "${spec.damageDice}" did not parse (${error instanceof Error ? error.message : String(error)}); ${rules.name} declares no damage rules to fall back on`;
                    }
                }
                return {
                    hit,
                    roll,
                    interpretation: {
                        ...interpretation,
                        meta: {
                            ...interpretation.meta,
                            action: actionName,
                            declarative: true,
                            damageModel,
                        },
                    },
                    ...(damage === undefined ? {} : { damage }),
                };
            },
            applyDamage(target, damage) {
                const character = target.character;
                if (!character) {
                    return { previousHp: 0, currentHp: 0, status: 'unknown', effects: [] };
                }
                // Affinity first. The system may declare no damage rules, but a
                // combatant can still arrive carrying resistances (a monster imported
                // from the bestiary), and the damage that lands is what the death
                // mechanic below keys off — an immune creature takes 0 and does not
                // trigger it.
                const { amount: appliedDamage, verdict } = applyAffinity(target.damageAffinity, damage.total, { type: damage.type, ...(damage.magical === undefined ? {} : { magical: damage.magical }) });
                const previousHp = character.health.current;
                const currentHp = Math.max(0, previousHp - appliedDamage);
                // Deliberately NOT written back to the character. The engine owns state
                // (ISS-0132): an adapter answers "what would this damage do", and the
                // caller decides whether it happened. previousHp/currentHp below carry
                // everything needed to apply it, so the write was redundant as well as
                // forbidden. Enforced by @machina/system-conformance, which caught this.
                const effects = [];
                // The one thing the format does say about hitting zero.
                if (currentHp === 0 && previousHp > 0 && combat.deathMechanic) {
                    effects.push(combat.deathMechanic);
                }
                return {
                    previousHp,
                    currentHp,
                    status: healthStatus(currentHp, character.health.max),
                    effects,
                    incomingDamage: damage.total,
                    appliedDamage,
                    affinity: verdict,
                };
            },
        };
    }
    // --- Magic and encounter balance are deliberately absent -------------------
    //
    // `MagicSystemSchema` names a casting style and its abilities and stops:
    // there is no slot table, no point pool, no cost. `EncounterBalanceSchema`
    // names a rating system and no maths. Implementing either from what is
    // declared would mean fabricating the numbers, and callers branch on the
    // surface being missing (`adapter.magic`, `adapter.balance`) precisely so a
    // system can decline. See the Phase 2 report for what these would need.
    //
    // RE-EXAMINED FOR ISS-0164, which added `getSpellSaveDC`,
    // `getSpellAttackBonus` and `listSpellAttacks` to the magic surface as
    // OPTIONAL methods, on the principle that a system unable to answer returns
    // `undefined` instead of a number. `magic.castingAbilities` plus
    // `abilityModFormula` looks like it finally gets a declarative system one of
    // those numbers. It does not, for two separate reasons, and both had to
    // fail before this block could stay as it is:
    //
    //  1. THE MATHS IS STILL SHORT TWO TERMS. A save DC is a base plus a
    //     training bonus plus the ability. The runtime has the ability — the
    //     third term — and nothing else. `GameSystemRules` has no proficiency
    //     formula at all (`getProficiencyBonus` is optional on the interface and
    //     this runtime does not emit it) and no DC base, so `8 + prof` would be
    //     5e's constants smuggled in under a generic name and applied to a
    //     system that never asked for them. Item 5 of ISS-0154, sharpened: the
    //     schema names the casting ability and still specifies no casting.
    //
    //  2. THE SURFACE IS ALL-OR-NOTHING, which decides it even if (1) were
    //     solved tomorrow. `getSpellSlots`, `validateCast` and `resolveCast` are
    //     REQUIRED on `magic`; only the three new methods are optional. Emitting
    //     `{ getSpellSaveDC }` alone will not type-check, and satisfying the
    //     three required ones from this schema means inventing a slot table — a
    //     bigger lie than the DC, told to get the DC out. This is item 9 of
    //     ISS-0154 (the combat surface is all-or-nothing) reappearing on magic,
    //     and making those three optional is a change to the interface for every
    //     system, not a change this runtime gets to make on its own.
    //
    // So the runtime still declines, and the DM guidance now says out loud that
    // the system declared magic the engine cannot compute — silence there is
    // where the model fills in 5e's spell slots from its own weights.
    return adapter;
}
/**
 * Would these rules build?
 *
 * For the SDK validator and the module installer, which want to report a broken
 * system as a validation error rather than by catching a throw.
 */
export function checkRules(rules) {
    try {
        adapterFromRules(rules);
        return { valid: true };
    }
    catch (err) {
        if (err instanceof DeclarativeSystemError) {
            return { valid: false, field: err.field, error: err.message };
        }
        if (err instanceof FormulaError) {
            return { valid: false, field: 'formula', error: err.message };
        }
        throw err;
    }
}
//# sourceMappingURL=adapter-from-rules.js.map